import { createAction, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import roomModel, { CreateRoomDto, Receiver, Room, RoomState, UpdateRoomDto } from '@/core/models/room.model'
import { CreateStaffDto } from '@/core/models/staff.model'

import { RootState } from '@/redux/rootReducer'

export type RoomReducerState = RoomState & {
  peerId: string | null
  roomsByConsultationId: Record<string, Room>
  roomsByGroupId: Record<string, Room>
}

export const roomAdapter = createEntityAdapter({ selectId: (room: Room) => room._id })
const roomSelector = roomAdapter.getSelectors((state: RootState) => state.room.rooms)

export const selectRoomById = roomSelector.selectById
export const selectRoomByConsultationId = (state: RootState, consultationId: string) => state.room.roomsByConsultationId[consultationId]
export const selectRoomByGroupId = (state: RootState, groupId: string) => state.room.roomsByGroupId[groupId]
export const selectPeerId = (state: RootState) => state.room.peerId

export const selectRoomGetRequestTracker = (state: RootState) => state.room.requestTracker.get
export const selectEmergencyRoomInvitationRequestTracker = (state: RootState) => state.room.requestTracker.sendEmergencyRoomInvitation

const initialState: RoomReducerState = {
  ...roomModel.getInitialState(),
  peerId: null,
  roomsByConsultationId: {},
  roomsByGroupId: {},
  requestTracker: {
    ...roomModel.getInitialState().requestTracker,
    createReceiver: { error: null, loading: false },
    sendEmergencyRoomInvitation: { error: null, loading: false },
  },
}

const roomSlice = createSlice({
  name: roomModel.reducerName,
  initialState,
  reducers: {
    addUserToRoom(state, action: PayloadAction<string>) {
      state.requestTracker.createReceiver.loading = true
    },
    addUserToRoomSuccess(state, action: PayloadAction<Receiver>) {
      const receiver = action.payload
      state.peerId = receiver.peerId
      state.requestTracker.createReceiver.error = null
      state.requestTracker.createReceiver.loading = false
    },
    addUserToRoomFailure(state, action) {
      state.peerId = null
      state.requestTracker.createReceiver.error = action.payload
      state.requestTracker.createReceiver.loading = false
    },
    createRoom: {
      reducer(state, action: PayloadAction<{ createRoomDto: CreateRoomDto; isEmergency?: boolean }>) {
        state.requestTracker.create.loading = true
      },
      prepare: (createRoomDto: CreateRoomDto, isEmergency?: boolean) => ({
        payload: { createRoomDto, isEmergency },
      }),
    },
    createRoomSuccess(state, action: PayloadAction<Room>) {
      roomAdapter.addOne(state.rooms, action.payload)
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createRoomFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    getRoom(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getRoomSuccess(state, action: PayloadAction<{ consultationId?: string; peerId: string | null; room: Room }>) {
      const { consultationId, peerId, room } = action.payload
      roomAdapter.setOne(state.rooms, room)
      if (!!consultationId) state.roomsByConsultationId[consultationId] = room
      if (room.groupId) state.roomsByGroupId[room.groupId] = room
      state.peerId = peerId
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getRoomFailure(state, action) {
      state.requestTracker.get.error = action.payload
      state.requestTracker.get.loading = false
    },
    updateRoom: {
      reducer: (state, action: PayloadAction<{ roomId: string; updateRoomDto: UpdateRoomDto }>) => {
        state.requestTracker.update.loading = true
      },
      prepare: (roomId: string, updateRoomDto: UpdateRoomDto) => ({
        payload: { roomId, updateRoomDto },
      }),
    },
    updateRoomSuccess(state, action: PayloadAction<Room>) {
      const room = action.payload
      const consultationId = typeof room.consultation === 'string' ? room.consultation : room.consultation?._id
      roomAdapter.setOne(state.rooms, action.payload)
      if (!!consultationId) state.roomsByConsultationId[consultationId] = room
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateRoomFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    sendEmergencyRoomInvitation: {
      reducer: (state, action: PayloadAction<{ roomId: string; guest: CreateStaffDto }>) => {
        state.requestTracker.sendEmergencyRoomInvitation.loading = true
      },
      prepare: (roomId: string, guest: CreateStaffDto) => ({
        payload: { roomId, guest },
      }),
    },
    sendEmergencyRoomInvitationSuccess(state) {
      state.requestTracker.sendEmergencyRoomInvitation.error = null
      state.requestTracker.sendEmergencyRoomInvitation.loading = false
    },
    sendEmergencyRoomInvitationFailure(state, action) {
      state.requestTracker.sendEmergencyRoomInvitation.error = action.payload
      state.requestTracker.sendEmergencyRoomInvitation.loading = false
    },
    mapRoomToConsultationIds(state, action: PayloadAction<{ room: Room; consultationIds: string[] }>) {
      for (const id of action.payload.consultationIds) {
        state.roomsByConsultationId[id] = action.payload.room
      }
    },
  },
})

export const emergencyRoomActiveSocketEvent = createAction<string>(`${roomModel.reducerName}/emergencyRoomActiveSocketEvent`)
export const emergencyRoomInactiveSocketEvent = createAction<string>(`${roomModel.reducerName}/emergencyRoomInactiveSocketEvent`)
export const emergencyRoomUpdatedSocketEvent = createAction<{ staffId: string; roomId: string }>(`${roomModel.reducerName}/emergencyRoomUpdatedSocketEvent`)
export const enterEmergencyRoomSocketEvent = createAction<string>(`${roomModel.reducerName}/enterEmergencyRoomSocketEvent`)
export const leaveEmergencyRoomSocketEvent = createAction<string>(`${roomModel.reducerName}/leaveEmergencyRoomSocketEvent`)

export const {
  addUserToRoom,
  addUserToRoomSuccess,
  addUserToRoomFailure,
  createRoom,
  createRoomSuccess,
  createRoomFailure,
  getRoom,
  getRoomSuccess,
  getRoomFailure,
  updateRoom,
  updateRoomSuccess,
  updateRoomFailure,
  sendEmergencyRoomInvitation,
  sendEmergencyRoomInvitationSuccess,
  sendEmergencyRoomInvitationFailure,
  mapRoomToConsultationIds,
} = roomSlice.actions

export default roomSlice.reducer
